// ProfileAndSubscription.tsx

import React from "react";
import { Box, Card, CardContent, Typography, Grid, Chip, Button } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Sparkles, CreditCard, Users, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ProfileSubscriptionProps {
  currentPlan: string;
  planPrice: number;
  billingCycle: string;
  nextBillingDate: string | null;
  storeCredit: number;
  totalReferrals: number;
}

export const ProfileSubscription: React.FC<{ data: ProfileSubscriptionProps }> = ({ data }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const PREFIX = "components.profileAndSubscription";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Current Active Plan Card */}
      <Card
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          background: (theme) =>
            theme.palette.mode === "light"
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(
                  theme.palette.secondary.main,
                  0.08,
                )} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(
                  theme.palette.background.paper,
                  0.9,
                )} 100%)`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "absolute", right: -20, top: -20, opacity: 0.1, color: "primary.main" }}>
          <Sparkles size={160} />
        </Box>
        <CardContent sx={{ p: 4, "&:last-child": { pb: 4 } }}>
          <Chip label={t(`${PREFIX}.activePlan`)} color="primary" size="small" sx={{ mb: 1, fontWeight: 600 }} />
          <Typography variant="h4" sx={{ color: "text.primary", mb: 1.75, fontWeight: 800 }}>
            {data.currentPlan}
          </Typography>
          <Typography variant="h5" sx={{ mt: 2, fontWeight: 700, color: "text.primary" }}>
            {t(`${PREFIX}.priceFormat`, { price: data.planPrice })}{" "}
            <Typography component="span" variant="body1" sx={{ color: "text.secondary" }}>
              / {data.billingCycle}
            </Typography>
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
            {t(`${PREFIX}.nextBillingDate`)}:{" "}
            <Box component="strong" sx={{ color: "text.primary" }}>
              {data.nextBillingDate}
            </Box>
          </Typography>
          <Button variant="contained" color="primary" endIcon={<ArrowUpRight size={16} />} sx={{ mt: 3, px: 3 }}>
            {t(`${PREFIX}.upgradeButton`)}
          </Button>
        </CardContent>
      </Card>

      {/* Financial Metrics Cards */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, bgcolor: "background.paper" }}
          >
            <CardContent sx={{ p: 3, display: "flex", alignItems: "center", gap: 2, "&:last-child": { pb: 3 } }}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  borderRadius: 2,
                  color: "success.main",
                }}
              >
                <CreditCard size={24} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t(`${PREFIX}.accumulatedStoreCredit`)}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
                  {t(`${PREFIX}.priceFormat`, { price: data.storeCredit.toFixed(2) })}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, bgcolor: "background.paper" }}
          >
            <CardContent sx={{ p: 3, display: "flex", alignItems: "center", gap: 2, "&:last-child": { pb: 3 } }}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  borderRadius: 2,
                  color: "primary.main",
                }}
              >
                <Users size={24} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t(`${PREFIX}.totalConvertingNetwork`)}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
                  {t(`${PREFIX}.usersCount`, { count: data.totalReferrals })}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
